import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import ProductionPlanningEmailRecipients from "../components/ProductionPlanningEmailRecipients";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { getBackendUrl } from "../config";

const BACKEND_URL = getBackendUrl();

const ProductionPlanning = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isSuperAdmin = user?.role === "SUPERADMIN";
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [hourlyData, setHourlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const isDark = document.documentElement.classList.contains("dark");

  const bgCard = isDark ? "bg-gray-800" : "bg-white";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-600";
  const borderColor = isDark ? "border-gray-700" : "border-gray-200";

  const getYesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axios.get(`${BACKEND_URL}/api/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = Array.isArray(res.data) ? res.data : [];
        setCompanies(list);
        if (list.length > 0) {
          setSelectedCompany((prev) => prev ?? list[0]);
        }
      } catch (e) {
        console.error("Error fetching companies:", e);
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  const handleSearch = async () => {
    if (!selectedCompany?.id || !selectedDate) return;
    setDataLoading(true);
    const token = localStorage.getItem("access_token");
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/planning/hourly-history?company_id=${encodeURIComponent(selectedCompany.id)}&date=${selectedDate}&start_hour=0&end_hour=23`,
        auth,
      );
      setHourlyData(normalizeHourlyPayload(res.data));
    } catch (e) {
      console.error("Error fetching hourly history:", e);
      setHourlyData(null);
    } finally {
      setDataLoading(false);
    }
  };

  const handleTomorrowForecast = async () => {
    if (!selectedCompany?.id) return;
    setDataLoading(true);
    const token = localStorage.getItem("access_token");
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/planning/production-forecast?company_id=${encodeURIComponent(selectedCompany.id)}&top_k=10`,
        auth,
      );
      const data = res.data;
      if (data?.date) setSelectedDate(data.date);
      setHourlyData(normalizeHourlyPayload(data));
    } catch (e) {
      console.error("Error fetching production forecast:", e);
      setHourlyData(null);
    } finally {
      setDataLoading(false);
    }
  };

  const formatValue = (v) => (v != null && v !== "" ? v : "—");

  const hasExportData =
    Array.isArray(hourlyData?.energy_meters) &&
    hourlyData.energy_meters.length > 0;

  const EXPORT_COLUMNS = [
    { key: "company", label: "Kompania" },
    { key: "date", label: "Data" },
    { key: "meter", label: "Energy Meter" },
    { key: "hour", label: "Ora" },
    { key: "production_kwh", label: "Prodhimi (kWh)" },
    { key: "ghi_wm2", label: "Rrezatimi GHI (W/m2)" },
    { key: "temp_ambient_c", label: "Temp. Ambient (C)" },
    { key: "temp_module_c", label: "Temp. Modul (C)" },
    { key: "wind_ms", label: "Era (m/s)" },
  ];

  const formatNumber = (value, digits = 2) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return n.toFixed(digits);
  };

  const formatExportValue = (key, value) => {
    if (value == null || value === "") return "—";
    if (key === "production_kwh") {
      return formatNumber(value, 2);
    }
    if (key === "ghi_wm2") return formatNumber(value, 1);
    if (key === "temp_ambient_c" || key === "temp_module_c")
      return formatNumber(value, 1);
    if (key === "wind_ms") return formatNumber(value, 1);
    return String(value);
  };

  const buildExportRows = () => {
    if (!hasExportData) return [];
    const rows = [];
    hourlyData.energy_meters.forEach((em) => {
      const meterName = em?.name || "—";
      const items = Array.isArray(em?.hourly_data) ? em.hourly_data : [];
      items.forEach((row) => {
        rows.push({
          company: selectedCompany?.name || "—",
          date: hourlyData?.date || selectedDate || "—",
          meter: meterName,
          hour: row?.hour ?? "—",
          production_kwh: row?.production_kwh ?? "",
          ghi_wm2: row?.ghi_wm2 ?? "",
          temp_ambient_c: row?.temp_ambient_c ?? "",
          temp_module_c: row?.temp_module_c ?? "",
          wind_ms: row?.wind_ms ?? "",
        });
      });
    });
    return rows;
  };

  const buildExportTableRows = () => {
    const rows = buildExportRows();
    return rows.map((row) =>
      EXPORT_COLUMNS.map((col) => formatExportValue(col.key, row[col.key])),
    );
  };

  const safeName = (value) =>
    String(value || "export").replace(/[^a-zA-Z0-9-_]+/g, "_");
  const isForecast = hourlyData?.mode === "forecast";
  const fileBase = `${safeName(selectedCompany?.name || "company")}_${safeName(hourlyData?.date || selectedDate || "date")}_${isForecast ? "forecast" : "planning"}`;
  const exportDocTitle = isForecast
    ? t("planningForecastExportTitle")
    : t("planningHistoryExportTitle");

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportCsv = () => {
    const rows = buildExportTableRows();
    if (!rows.length) return;
    const headers = EXPORT_COLUMNS.map((c) => c.label);
    const escapeCsv = (v) => {
      const s = String(v ?? "");
      if (s.includes('"') || s.includes(";") || s.includes("\n"))
        return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const content = [
      headers.map(escapeCsv).join(";"),
      ...rows.map((r) => r.map(escapeCsv).join(";")),
    ].join("\n");
    const withBom = `\uFEFF${content}`;
    downloadBlob(
      new Blob([withBom], { type: "text/csv;charset=utf-8;" }),
      `${fileBase}.csv`,
    );
    setShowExportMenu(false);
  };

  const exportExcel = () => {
    const rows = buildExportTableRows();
    if (!rows.length) return;
    const headers = EXPORT_COLUMNS.map((c) => c.label);
    const escHtml = (v) =>
      String(v ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    const html = `
      <html>
        <head>
          <meta charset="UTF-8" />
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .title { font-size: 18px; font-weight: 700; margin-bottom: 4px; }
            .meta { font-size: 12px; color: #475569; margin-bottom: 12px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #cbd5e1; padding: 6px 8px; font-size: 12px; text-align: left; }
            th { background: #e2e8f0; font-weight: 700; }
            tr:nth-child(even) td { background: #f8fafc; }
          </style>
        </head>
        <body>
          <div class="title">${escHtml(exportDocTitle)}</div>
          <div class="meta">Kompania: ${escHtml(selectedCompany?.name || "—")} | Data: ${escHtml(hourlyData?.date || selectedDate || "—")}</div>
          <table>
            <thead><tr>${headers.map((h) => `<th>${escHtml(h)}</th>`).join("")}</tr></thead>
            <tbody>${rows.map((r) => `<tr>${r.map((v) => `<td>${escHtml(v)}</td>`).join("")}</tr>`).join("")}</tbody>
          </table>
        </body>
      </html>
    `;
    downloadBlob(
      new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" }),
      `${fileBase}.xls`,
    );
    setShowExportMenu(false);
  };

  const exportPdf = () => {
    const rows = buildExportTableRows();
    if (!rows.length) return;
    const headers = EXPORT_COLUMNS.map((c) => c.label);
    const escHtml = (v) =>
      String(v ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`
      <html>
        <head>
          <title>${fileBase}</title>
          <style>
            @page { size: A4 landscape; margin: 12mm; }
            body { font-family: Arial, sans-serif; color: #0f172a; }
            .header { margin-bottom: 10px; }
            .title { font-size: 18px; font-weight: 700; margin: 0 0 4px; }
            .meta { font-size: 12px; color: #475569; margin: 0; }
            table { border-collapse: collapse; width: 100%; table-layout: fixed; }
            th, td { border: 1px solid #cbd5e1; padding: 4px 6px; font-size: 10px; word-wrap: break-word; }
            th { background: #e2e8f0; text-align: left; }
            tbody tr:nth-child(even) td { background: #f8fafc; }
          </style>
        </head>
        <body>
          <div class="header">
            <p class="title">${escHtml(exportDocTitle)}</p>
            <p class="meta">Kompania: ${escHtml(selectedCompany?.name || "—")} | Data: ${escHtml(hourlyData?.date || selectedDate || "—")}</p>
          </div>
          <table>
            <thead><tr>${headers.map((h) => `<th>${escHtml(h)}</th>`).join("")}</tr></thead>
            <tbody>${rows.map((r) => `<tr>${r.map((v) => `<td>${escHtml(v)}</td>`).join("")}</tr>`).join("")}</tbody>
          </table>
        </body>
      </html>
    `);
    w.document.close();
    w.focus();
    w.print();
    setShowExportMenu(false);
  };

  const parseHourFromRow = (rowHour) => {
    if (typeof rowHour === "number" && Number.isFinite(rowHour)) {
      return Math.max(0, Math.min(23, Math.floor(rowHour)));
    }
    const raw = String(rowHour ?? "").trim();
    if (!raw) return null;
    const direct = raw.match(/^(\d{1,2})(?::\d{2})?/);
    if (direct) {
      const h = Number(direct[1]);
      if (Number.isFinite(h) && h >= 0 && h <= 23) return h;
    }
    const anyNum = raw.match(/(\d{1,2})/);
    if (anyNum) {
      const h = Number(anyNum[1]);
      if (Number.isFinite(h) && h >= 0 && h <= 23) return h;
    }
    return null;
  };

  const withFullDayHours = (rows) => {
    const src = Array.isArray(rows) ? rows : [];
    const byHour = new Map();
    src.forEach((row) => {
      const h = parseHourFromRow(row?.hour);
      if (h == null) return;
      byHour.set(h, row || {});
    });
    return Array.from({ length: 24 }, (_, h) => {
      const base = byHour.get(h) || {};
      return { ...base, hour: `${String(h).padStart(2, "0")}:00` };
    });
  };

  const normalizeHourlyPayload = (payload) => {
    if (!payload || typeof payload !== "object") return payload;
    const meters = Array.isArray(payload.energy_meters)
      ? payload.energy_meters
      : [];
    return {
      ...payload,
      energy_meters: meters.map((em) => ({
        ...em,
        hourly_data: withFullDayHours(em?.hourly_data),
      })),
    };
  };

  return (
    <Layout>
      <div className="p-4 md:p-6">
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
            {t("productionPlanning")}
          </h1>
        </div>

        {isSuperAdmin && <ProductionPlanningEmailRecipients />}

        {/* Company & Date Selection */}
        <div
          className={`${bgCard} rounded-xl p-4 shadow-sm border ${borderColor} mb-6`}
        >
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t("company")}
                </label>
                <select
                  value={selectedCompany?.id ?? ""}
                  onChange={(e) => {
                    const c = companies.find((x) => x.id === e.target.value);
                    setSelectedCompany(c || null);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="">{t("selectCompanyFirst")}</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t("selectDate")}
                </label>
                <div className="flex gap-2 flex-wrap items-center">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="flex-1 min-w-[140px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setSelectedDate(getYesterday())}
                    className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    {t("yesterday")}
                  </button>
                  <button
                    type="button"
                    onClick={handleTomorrowForecast}
                    disabled={!selectedCompany?.id || dataLoading}
                    className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    {t("tomorrow")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={
                      !selectedCompany?.id || !selectedDate || dataLoading
                    }
                    className="px-4 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("search")}
                  </button>
                </div>
              </div>
            </div>
            <div className="relative self-end lg:self-auto">
              <button
                type="button"
                onClick={() => setShowExportMenu((v) => !v)}
                disabled={!hasExportData}
                title="Export"
                aria-label="Export"
                className="w-9 h-9 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 5v10m0 0l-4-4m4 4l4-4M5 19h14"
                  />
                </svg>
              </button>
              {showExportMenu && hasExportData && (
                <div className="absolute right-0 mt-2 w-36 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg z-20 overflow-hidden">
                  <button
                    type="button"
                    onClick={exportExcel}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Excel
                  </button>
                  <button
                    type="button"
                    onClick={exportPdf}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    PDF
                  </button>
                  <button
                    type="button"
                    onClick={exportCsv}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    CSV
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-8 text-gray-500">{t("loading")}</div>
        ) : !selectedCompany ? (
          <div
            className={`${bgCard} rounded-xl p-8 text-center border ${borderColor}`}
          >
            <p className="text-gray-500 dark:text-gray-400">
              {t("selectCompanyForData")}
            </p>
          </div>
        ) : dataLoading ? (
          <div className="text-center py-8 text-gray-500">
            {t("loadingData")}
          </div>
        ) : hourlyData == null ? (
          <div
            className={`${bgCard} rounded-xl p-8 text-center border ${borderColor}`}
          >
            <p className="text-gray-500 dark:text-gray-400">
              {t("planningEmptyHint")}
            </p>
          </div>
        ) : !hourlyData?.energy_meters?.length ? (
          <div
            className={`${bgCard} rounded-xl p-8 text-center border ${borderColor}`}
          >
            {hourlyData?.mode === "forecast" ? (
              <p className="text-gray-500 dark:text-gray-400">
                {hourlyData?.message || t("noEnergyMeters")}
              </p>
            ) : (
              <>
                <svg
                  className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                <p className="text-gray-500 dark:text-gray-400">
                  {t("noEnergyMeters")}
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  {t("weatherHint")}
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {hourlyData.energy_meters.map((em) => (
              <div
                key={em.id}
                className={`${bgCard} rounded-xl shadow-sm border ${borderColor} overflow-hidden`}
              >
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="font-semibold text-gray-900 dark:text-white">
                    {em.name}
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">
                          {t("hour")}
                        </th>
                        <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">
                          {t("emProduction")}
                        </th>
                        <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">
                          {t("radiationGhi")}
                        </th>
                        <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">
                          {t("tempAmbientC")}
                        </th>
                        <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">
                          {t("tempModuleC")}
                        </th>
                        <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">
                          {t("windSpeed")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {em.hourly_data.map((row, idx) => (
                        <tr
                          key={idx}
                          className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                        >
                          <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">
                            {row.hour}
                          </td>
                          <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                            {formatValue(row.production_kwh)}
                          </td>
                          <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                            {formatValue(row.ghi_wm2)}
                          </td>
                          <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                            {formatValue(row.temp_ambient_c)}
                          </td>
                          <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                            {formatValue(row.temp_module_c)}
                          </td>
                          <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                            {formatValue(row.wind_ms)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProductionPlanning;
